import React, { FC } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import styled from 'styled-components';
import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import SubmitButton from 'components/SubmitButton';
import { useSendEmailPin } from 'modules/corporateOffer/hooks/useSendEmailPin';

import Input from '../inputs/Input';
import SuccessResult from '../SuccessResult';

import { LinkContainer, NoteText, PasscodeRecoveryLink } from './SignInStep';
import { OnboardingSubtitle, OnboardingTitle } from './StyledComponents';
import { EMAIL_INPUT_ID, emailValidationSchema } from './validationSchemas';
import { Redline } from './VerifyEmailStep';

const validationSchema = yup.object().concat(emailValidationSchema);

type ForgotPasswordData = yup.InferType<typeof validationSchema>;

type Props = {
  onSuccess: (email: string) => void;
  onKnowPassword: () => void;
};

const ForgotPasswordStep: FC<Props> = ({ onSuccess, onKnowPassword }) => {
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    register,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      EMAIL_INPUT_ID: '',
    },
  });

  const { mutate, isLoading, error } = useSendEmailPin({
    onSuccess: () => {
      onSuccess(getValues().EMAIL_INPUT_ID);
    },
  });

  const onSubmit = async (data: ForgotPasswordData) => {
    mutate({
      email: data.EMAIL_INPUT_ID,
    });
  };

  return (
    <Container>
      {isLoading ? (
        <SuccessResult
          title="We have emailed you a temporary PIN code"
          subtitle="Please enter this to set a new passcode"
        />
      ) : (
        <>
          <header>
            <OnboardingTitle title="Forgot passcode?" />
            <OnboardingSubtitle subtitle="Please enter your email to receive a temporary PIN" />
            <Redline />
          </header>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Input<ForgotPasswordData>
              label="Email"
              inputId={EMAIL_INPUT_ID}
              inputType="email"
              control={control}
              register={register}
              error={
                error?.message
                  ? ({ message: error.message } as FieldError)
                  : errors.EMAIL_INPUT_ID
              }
            />

            <ButtonWrapper data-testid="submit-button-wrapper">
              <SubmitButton
                text="SIGN IN"
                isDisabled={Object.keys(errors).length > 0 || isLoading}
                borderSize="xl"
                buttonSize="xl"
                fontSize="md"
              />
            </ButtonWrapper>
          </Form>
          <LinkContainer>
            <PasscodeRecoveryLink onClick={onKnowPassword}>
              I know my passcode
            </PasscodeRecoveryLink>
          </LinkContainer>
          <DashedLine />
          <NoteText>
            This page is only for managing your account details.{'\n'}To use
            Shido, download the app to your phone and sign in there.
          </NoteText>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 100%;
  }
`;

const Form = styled.form`
  margin: 70px auto 0;
  max-width: 600px;
  padding-left: 20px;
  padding-right: 20px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: 335px;
    margin: 33px auto 0;
    padding-left: 0;
    padding-right: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }
`;

const ButtonWrapper = styled.div`
  margin: 220px auto 0;
  display: flex;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin: 0;
  }
`;

const DashedLine = styled.div`
  width: 600px;
  height: 1px;
  background-image: linear-gradient(to right, transparent 50%, #6c6262 50%);
  background-size: 8px 100%;
  opacity: 0.4;
  margin: 39px auto 0;

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: 93%;
    margin: 42px auto 0;
  }
`;

export default ForgotPasswordStep;
